import url from 'url';
import uri from 'urijs';
import { Response } from 'node-fetch';

import { prodBackend, httpProtocol } from './constants';
import fetch from './fetch';
import { leaveQueryBreadCrumb } from './helpers';
import { ObjectType } from '@vromo/models/helpers/models';
import { AccountStatusType } from '@vromo/models';

export interface ApiEndpoint<R = any, B = ObjectType> {
    get: (queryParams?: ObjectType, options?: ObjectType, token?: string) => Promise<R>
    post: (bodyData: B, options?: ObjectType, token?: string) => Promise<R>
}




interface GenericResponse {
    data: string
    status: string
    status_code: number
    success: boolean
}


/** vromo Auth api methods */
export class API {

    private _host: string;

    constructor(host = prodBackend) {
        this._host = host;
    }

    /** R= result type, B= post body type */
    private _apiEndpoint<R = any, B = ObjectType>(path: string, bodyType: "json" | "string"): ApiEndpoint<R, B> {
        const endpointUrl = url.resolve(`${httpProtocol}://${this._host}/`, path)

        return {
            get: async (queryParams = {}, options = {}, token?) => {
                const processedUrl = uri(endpointUrl).query({ ...token && { tok: token }, ...queryParams }).toString();
                const getQueryData: Response = await fetch(processedUrl, { method: 'GET', ...options })
                return (await getQueryData.json()) as R;
            },
            post: async (bodyData: B, options = {}, token?) => {
                const processedUrl = uri(endpointUrl).query({ ...token && { tok: token } }).toString();
                const body = bodyType === "json" ?
                    JSON.stringify(bodyData) :
                    uri.buildQuery(bodyData)

                leaveQueryBreadCrumb({ data: body, path: processedUrl, type: 'request', protocol: 'post' })
                const response: Response = await fetch(processedUrl, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    },
                    ...options,
                    body
                });
                const responseBody: R = await response.json().catch(() => ({})) as R
                if (!response.ok) {
                    const error = new Error(`HTTP Code '${response.status}': ${response.statusText}`);
                    return Promise.reject(new ApiError(responseBody, error))
                }
                return responseBody
            }
        }
    }


    get auth(): ApiEndpoint<{
        success: boolean,
        token: string,
        host: string
    }, { name: string, pass: string }> {
        return this._apiEndpoint('/v2/auth', 'json')
    }

    /** path is built as `/v2/account/status?tok=[login token]&group=[current group ID]` */
    get account(): ApiEndpoint<{
        /** top level group ID */
        group: number
        /** when this group was created? 
         * @note Maybe missing if group is too old */
        created?: number
        // todo: need to add the other account status
        // todo: need to add the account features
        /** whether this is group is on trial or not
         * @note Maybe missing if group is too old */
        type?: AccountStatusType

    }> {
        return this._apiEndpoint('/v2/account/status', 'string');
    }
    /** returns the 1 use authorization code for requesting vromo access Tokens */
    get oAuthAuthorize(): ApiEndpoint<Partial<GenericResponse>, {
        group?: number; client?: string;
    }> {
        return this._apiEndpoint('/v2/oauth/authorize', 'json');
    }

    /** returns a new integration token */
    get oAuthAccess(): ApiEndpoint<{ access_token: string, refresh_token: string }, {
        code: string,
        grant_type: 'authorization_code'
    }> {
        return this._apiEndpoint('/v2/oauth/access', 'string');
    }
    get resetPassword(): ApiEndpoint<{ password: string }, ResetPasswordAnswer> {
        return this._apiEndpoint('/v2/reset-password', 'json');
    }
    get changePassword(): ApiEndpoint {
        return this._apiEndpoint('/v2/change-password', 'json');
    }
    get checkUsername(): ApiEndpoint {
        return this._apiEndpoint('/v2/check-username', 'string');
    }
    get verifyZego(): ApiEndpoint {
        return this._apiEndpoint('/v2/zego/verified', 'string');
    }
}


/** rest api Error ( includes error body object response details ) */
export class ApiError<B> extends Error {
    protected _response: B;

    constructor(response: B, payload: Error | string) {
        super(
            (payload as Error).message ||
            (payload || "undefined err").toString()
        );
        this._response = response;
    }

    get response(): B {
        return this._response;
    }
}




declare global {
    interface Window {
        API: API;
    }
}




export type ResetPasswordAnswer = { status: string, status_code: number, success: boolean }
